import { useContext, useEffect } from 'react';
import { AppContext } from '../../Store';
import { ChallengeContext } from '../../challenge-context';
import { getAssetUrl } from '../../helpers/get-asset-url';
import workerSrc from '../../workers/pow_worker.ts';
import { OneLPSsdkParamsContext } from '../../onelps-sdk-params-context';
import onelps from '../../api';
import { isChallengeFailure, ChallengeFailedErrorCode } from '../../api/actions/challenge-failure';

const getPromiseWithResolvers = () => {
	let resolve;
	let reject;

	const promise = new Promise((r, re) => {
		resolve = r;
		reject = re;
	});

	return { promise, resolve, reject };
};

const solveWithWorker = (workerUrl, challengeConfig) => {
	const { promise, resolve, reject } = getPromiseWithResolvers();
	const worker = new Worker(workerUrl);

	const handler = (event) => {
		if (event.data?.type === 'POW_SOLVED') {
			worker.removeEventListener('message', handler);
			resolve({ solved: true, solution: event.data.solution });
		}

		if (event.data?.type === 'POW_TIMEOUT') {
			worker.removeEventListener('message', handler);
			resolve({ solved: false });
		}

		if (event.data?.type === 'POW_ERROR') {
			worker.removeEventListener('message', handler);
			reject(new Error(event.data.error));
		}
	};

	worker.addEventListener('message', handler);
	worker.postMessage({
		type: 'POW_START_SOLVE',
		challenge: challengeConfig.challenge,
		difficulty: challengeConfig.difficulty,
		challengeId: challengeConfig.id,
		timeout: challengeConfig.timeout
	});

	return { worker, promise };
};

const OnelpsChallengeSolver = () => {
	const [, dispatch] = useContext(AppContext);

	const params = useContext(OneLPSsdkParamsContext);
	const challengePromiseRef = useContext(ChallengeContext);

	const url = getAssetUrl() + workerSrc;
	useEffect(() => {
		const config = params.challengeConfig;
		// If this is not defined it means there is no challenge needed for this market
		if (!config) {
			return;
		}

		const { promise: statePromise, resolve: resolveState, reject: rejectState } = getPromiseWithResolvers();
		if (!config.dryRun) {
			// We only want to wait for the promise when it's required for login.
			// Otherwise, we are just interested in the metrics
			challengePromiseRef.current = statePromise;
		}

		let cancelled = false;
		let currentWorker = undefined;

		const failChallenge = (failure) => {
			rejectState(failure);
		};

		(async () => {
			try {
				const data = await fetch(url);
				const blob = await data.blob();
				const workerUrl = URL.createObjectURL(blob);

				try {
					const maxRetries = config.maxRetries ?? 0;
					let activeChallenge = config;
					let attempt = 0;
					let solution;

					while (!cancelled) {
						const { worker, promise } = solveWithWorker(workerUrl, activeChallenge);
						currentWorker = worker;
						const result = await promise;
						worker.terminate();
						currentWorker = undefined;

						if (result.solved) {
							solution = result.solution;
							break;
						}

						if (attempt >= maxRetries) {
							failChallenge({
								errors: [{
									code: ChallengeFailedErrorCode,
									message: 'Challenge could not be solved within the maximum number of retries',
								}],
							});
							return;
						}

						attempt += 1;
						const reissued = await onelps.actions.issueChallenge();
						if (isChallengeFailure(reissued)) {
							failChallenge(reissued);
							return;
						}
						activeChallenge = reissued;
					}

					if (cancelled) {
						return;
					}

					// keep for debugging, on prod this will be striped out
					console.log(`Challenge solved with solution ${solution}`);
					const redeemed = await onelps.actions.redeemChallenge({
						challengeId: activeChallenge.id,
						solution: solution,
					});
					if (isChallengeFailure(redeemed)) {
						failChallenge(redeemed);
						return;
					}
					resolveState();
				} finally {
					URL.revokeObjectURL(workerUrl);
				}
			} catch (e) {
				rejectState(e);
			}
		})();

		return () => {
			cancelled = true;
			currentWorker?.terminate();
			challengePromiseRef.current = null;
		};
	}, [dispatch, params.challengeConfig, url, challengePromiseRef]);

	return null;
};

export default OnelpsChallengeSolver;
